/**
 * PATH: apps/quote/src/pages/pricelist-page/MaterialsTab.tsx
 * PURPOSE: Material list rolled up from the org's quotes + one-click PO issuance
 *          for stock ordering (draft POs grouped by supplier).
 * WHY: Operator 2026-06-14 — "material list out of quotes integrated to PO
 *      issuance for stock ordering."
 * FLOW: getMaterialTakeoff → editable-ish list → issueMaterialPO → draft POs.
 * RELATED: pricing-api (getMaterialTakeoff/issueMaterialPO), BE material_takeoff
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, ShoppingCart, Package, CheckCircle2 } from 'lucide-react';
import { quoteApi } from '@/lib/quoteApi';
import { formatCurrency } from '@/lib/utils';

export default function MaterialsTab() {
  const [groupBySupplier, setGroupBySupplier] = useState(true);
  const [result, setResult] = useState<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['material-takeoff'],
    queryFn: () => quoteApi.getMaterialTakeoff(),
    staleTime: 60000,
  });

  const issueMutation = useMutation({
    mutationFn: () => quoteApi.issueMaterialPO({ group_by_supplier: groupBySupplier }),
    onSuccess: (r) => setResult(r),
  });

  const materials = data?.materials ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-gray-500 text-sm">
            Materials rolled up from all of this org's job quotes — review, then issue purchase orders for stock ordering.
          </p>
          {data && (
            <p className="text-xs text-gray-400 mt-1">
              {data.material_count} materials · est. total {formatCurrency(data.total_est_cost)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <input type="checkbox" checked={groupBySupplier} onChange={() => setGroupBySupplier((v) => !v)} />
            One PO per supplier
          </label>
          <button
            onClick={() => issueMutation.mutate()}
            disabled={issueMutation.isPending || !materials.length}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {issueMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
            Issue purchase orders
          </button>
        </div>
      </div>

      {result && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-emerald-800 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> Created {result.created} draft purchase order(s)
          </div>
          <ul className="mt-1 text-emerald-700 dark:text-emerald-400 text-xs space-y-0.5">
            {(result.purchase_orders ?? []).map((po: any) => (
              <li key={po.id}>{po.supplier}: {po.line_count} line(s), {formatCurrency(Number(po.total))} — {po.status}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : !materials.length ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            No materials found in quotes yet. Create some job quotes first.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Material</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Supplier</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Qty</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Unit</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Est. cost</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">In quotes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {materials.slice(0, 200).map((m, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-3 text-gray-900 dark:text-white">{m.product}</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">{m.supplier || '—'}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{m.qty}</td>
                  <td className="px-6 py-3 text-gray-500">{m.unit}</td>
                  <td className="px-6 py-3 text-right font-medium tabular-nums">{formatCurrency(m.est_cost)}</td>
                  <td className="px-6 py-3 text-center text-gray-500">{m.occurrences}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
